import React from 'react';
import { CapabilityNoetivraNote } from './CapabilityNoetivraNote';
import { motion } from 'framer-motion';
import { Crown, Target, CheckCircle, XCircle } from 'lucide-react';
export function LeadershipResponsibilityPage() {
  return <div className="min-h-screen bg-surface-bg" data-id="element-809" data-mp="3vbu45oe2fzd9">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-810" data-mp="3qa4pzzvjr5ky">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-811" data-mp="84rsym0z6cc7">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-812" data-mp="cvq8hje536ee">
            <span className="w-8 h-px bg-teal" data-id="element-813" data-mp="cycnu579uq76"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-814" data-mp="qx2ofutav80a">
              Capability
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-815" data-mp="mwaawpdeagy7"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-816" data-mp="2xggpkbwqtd7j">
            Leadership Responsibility
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-817" data-mp="36fkc5pv4caqs">
            At SyNeural, leadership responsibility refers to the ability to
            carry power, influence, and consequence in a way that protects
            larger systems rather than destabilizing them. It involves making
            decisions with awareness of broader effects, accepting
            accountability, managing complexity, and acting with a sense of duty
            toward the institution, community, or system being led.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-818" data-mp="2zwp8wqmavug">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-819" data-mp="7yhg45jzc368">
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-820" data-mp="3o59mtmj5t9ix">
            Leadership responsibility is not simply about authority, confidence,
            or visibility. It is about whether someone can handle larger
            consequence without collapsing into ego, impulsivity, factionalism,
            or short-term thinking. As responsibility increases, so does the
            cost of poor judgment. That is why leadership responsibility sits at
            the top of the SyNeural capability model.
          </p>
        </motion.div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-821" data-mp="2cqcwm9b8oe9z">
        <div className="max-w-4xl mx-auto" data-id="element-822" data-mp="2cs7etfv4lsad">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-823" data-mp="27sbbliu964vv">
            <div className="flex items-center gap-4 mb-6" data-id="element-824" data-mp="dhbi4yoy170f">
              <div className="w-12 h-12 rounded-lg bg-navy flex items-center justify-center text-white" data-id="element-825" data-mp="ih7pn7wungkq">
                <Target size={24} data-id="element-826" data-mp="4hybeds7ewje" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-827" data-mp="8k31x3ncvd1t">
                Why It Matters
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-828" data-mp="3qr96s04a0wk5">
              Leadership judgment scales. A poor decision at the top can affect
              teams, institutions, communities, or entire systems. Weak
              leadership responsibility can produce instability, mistrust,
              internal damage, and preventable crises. Strong leadership
              responsibility supports continuity, resilience, long-term
              planning, and healthier institutions. It is one of the clearest
              distinctions between influence and readiness.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Strong Leadership Responsibility Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-829" data-mp="3c0j72c9rkcb4">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-830" data-mp="372zvxl8d4fne">
          <div className="flex items-center gap-4 mb-6" data-id="element-831" data-mp="2tbuqu3m86v5o">
            <div className="w-12 h-12 rounded-lg bg-teal flex items-center justify-center text-white" data-id="element-832" data-mp="2ob73pt14aygq">
              <CheckCircle size={24} data-id="element-833" data-mp="2jcnhdrim3knu" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-834" data-mp="2jd5j33o1mr35">
              What Strong Leadership Responsibility Looks Like
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-835" data-mp="13sd2mjpz2k10">
            People strong in leadership responsibility are able to absorb
            pressure, think beyond personal image, handle dissent, weigh
            consequences, and act with regard for the larger system rather than
            immediate ego needs. They understand that leadership is not merely
            about being followed. It is about stewarding complexity, maintaining
            trust, and carrying responsibility without destabilizing the
            structure around them.
          </p>
        </motion.div>
      </section>

      {/* What Weak Leadership Responsibility Looks Like */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white border-y border-line" data-id="element-836" data-mp="3tizt0qh6k1m8">
        <div className="max-w-4xl mx-auto" data-id="element-837" data-mp="1exma74v8u8b">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-838" data-mp="1dmkgcuzdwju">
            <div className="flex items-center gap-4 mb-6" data-id="element-839" data-mp="1ehh61uvmuh34">
              <div className="w-12 h-12 rounded-lg bg-navy-light flex items-center justify-center text-white" data-id="element-840" data-mp="19hw6bi8n24pc">
                <XCircle size={24} data-id="element-841" data-mp="lrimug44de6d" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-842" data-mp="gsfi6ws1x9yx">
                What Weak Leadership Responsibility Looks Like
              </h2>
            </div>
            <p className="text-lg text-ink-muted leading-relaxed" data-id="element-843" data-mp="qq3e3q1e8nio">
              Weak leadership responsibility often appears as impulsive
              decision-making, image management, blame shifting, inability to
              absorb criticism, factional behavior, and failure to recognize how
              decisions affect others downstream. In institutions and governance
              settings, this can produce instability, poor succession,
              fragmented trust, and repeated strategic error.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How It Can Be Strengthened */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-844" data-mp="24p562aajnrsg">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-845" data-mp="24rhvt9mjn6g2">
          <div className="flex items-center gap-4 mb-6" data-id="element-846" data-mp="2no9ebgu9hhj2">
            <div className="w-12 h-12 rounded-lg bg-green-soft flex items-center justify-center text-white" data-id="element-847" data-mp="2nmy7f85irtnq">
              <Crown size={24} data-id="element-848" data-mp="3bc0pui7z9qja" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-ink-dark" data-id="element-849" data-mp="3g53miz81xg3p">
              How It Can Be Strengthened
            </h2>
          </div>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-850" data-mp="1g4oxur0av7pd">
            Leadership responsibility can be strengthened through systems
            thinking, accountability structures, crisis simulations, mentorship,
            consequence review, role-based training, and experience in carrying
            progressively larger responsibility with reflection and support. In
            the SyNeural framework, it is treated as a developmental capability
            that becomes increasingly important as influence, consequence, and
            public trust expand.
          </p>
        </motion.div>
      </section>
      <CapabilityNoetivraNote capability="Leadership responsibility" data-id="element-851" data-mp="1l1nr77c53ami" />
    </div>;
}